import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useMediumUpBp } from '../../hooks/useMedia';
import Accordion from '../accordion/Accordion';
import CTAButton from '../ctaButton/CTAButton';
import { LazyLoadImage } from 'react-lazy-load-image-component';

const FluidColumns = (props) => {
  const {
    title,
    subtitle,
    titleColour,
    titleAlignment,
    colourScheme,
    columnsWidth,
    columns,
    children,
    breakPoint,
    reverse,
    sectionId,
    bigTitle,
    wrap,
    alignItems,
    customPadding,
    itemsWidth,
    overflow,
    mobileAccordion,
    accordionTheme,
    accordionExtraPadding,
    accordionShowBackgroundOnExpand,
    backgroundImage,
    hideImagesOnTablet,
    postColumnsCta,
    styleHeadingAs
  } = props;

  const isMediumUp = useMediumUpBp();

  const componentClasses = classNames('fluid-columns', {
    [`fluid-columns--${colourScheme}-scheme`]: colourScheme,
    [`fluid-columns--padding-${customPadding}`]: customPadding,
    'fluid-columns--reverse': reverse,
    [`titles--${titleColour}`]: titleColour,
    [`fluid-columns--break-${breakPoint}`]: breakPoint,
    'fluid-columns--break-md': !breakPoint,
    [`fluid-columns--wrap-${wrap}`]: wrap,
    [`fluid-columns--items-width-${itemsWidth}`]: itemsWidth,
    [`fluid-columns--overflow-${overflow}`]: overflow,
    'fluid-columns--accordion-extra-padding': accordionExtraPadding,
    'fluid-columns--hide-img-tablet': hideImagesOnTablet
  });

  let content = columns || children;

  const accordionContent = (textProp = '') => {
    const heading = textProp.match(/^(<h3>.*?<\/h3>)/g);
    const headingStripTags = heading[0].replace(/<[^>]+>/g, '');
    const body = heading ? textProp.slice(heading[0].length) : '';
    return heading
      ? {
          heading: headingStripTags,
          body
        }
      : null;
  };

  return (
    <section className={componentClasses} id={sectionId}>
      {backgroundImage ? (
        <div className="fluid-columns__background-image">
          <LazyLoadImage alt="background" src={backgroundImage} />
        </div>
      ) : null}
      <div
        className={classNames('fluid-columns__wrapper', {
          [`fluid-columns__wrapper--${customPadding}`]: customPadding
        })}
      >
        {bigTitle && (
          <h2
            className={classNames('fluid-columns-big-title', {
              [`fluid-columns-big-title--${titleAlignment}`]: titleAlignment,
              [`as-${styleHeadingAs}`]: styleHeadingAs
            })}
          >
            {bigTitle}
          </h2>
        )}
        {title && (
          <h3
            className={classNames('fluid-columns-title', {
              [`fluid-columns-title--${titleAlignment}`]: titleAlignment
            })}
          >
            {title}
          </h3>
        )}
        {subtitle && (
          <div className="fluid-columns-paragraph">
            <p>{props.subtitle}</p>
          </div>
        )}
        <div
          className={classNames('fluid-columns__inner', {
            [`fluid-columns__inner-${columnsWidth}-cols-width`]: columnsWidth,
            [`fluid-columns__inner--align-items-${alignItems}`]: alignItems
          })}
        >
          {content.map((child) => {
            if (!isMediumUp && mobileAccordion) {
              const accordionProps = accordionContent(child.props.text || child.props[child.props.src]);
              return accordionProps ? (
                <div key={`${accordionProps.heading}-${accordionProps.body}`} className="accordion">
                  <Accordion
                    key={`${accordionProps.heading}-${accordionProps.body}`}
                    id={accordionProps.heading}
                    heading={accordionProps.heading}
                    showBackgroundOnExpand={accordionShowBackgroundOnExpand === 'true' && true}
                    theme={accordionTheme}
                  >
                    <div dangerouslySetInnerHTML={{ __html: accordionProps.body }} />
                  </Accordion>
                </div>
              ) : null;
            } else {
              return child;
            }
          })}
        </div>

        {postColumnsCta && <CTAButton {...postColumnsCta} />}
      </div>
    </section>
  );
};

FluidColumns.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  titleColour: PropTypes.oneOf(['purple', 'teal', 'dark-navy', 'navy']),
  colourScheme: PropTypes.oneOf(['dark-navy', 'teal', 'white', 'light-grey', 'sand', 'neutral-navy']),
  columns: PropTypes.array,
  breakPoint: PropTypes.string,
  reverse: PropTypes.string,
  sectionId: PropTypes.string,
  bigTitle: PropTypes.string,
  wrap: PropTypes.oneOf(['2', '3']),
  alignItems: PropTypes.oneOf(['top', 'center']),
  padding: PropTypes.oneOf(['top', 'bottom', 'top-bottom']),
  itemsWidth: PropTypes.oneOf(['full']),
  overflow: PropTypes.oneOf(['none']),
  mobileAccordion: PropTypes.string,
  accordionTheme: PropTypes.string,
  backgroundImage: PropTypes.string,
  hideImagesOnTablet: PropTypes.string,
  postColumnsCta: PropTypes.object,
  styleHeadingAs: PropTypes.oneOf(['h2', 'h3'])
};

export default FluidColumns;
